// Anonymous session id + inbound attribution capture.
//
// Security (plan §14): the session id is opaque random — never derived from
// anything about the visitor — and attribution values are treated as opaque
// tokens: charset-validated, length-capped, never parsed. No email, ever.

import { ownProperties } from './api';

export const SID_KEY = 'mdfs_sid';
export const ATTR_KEY = 'mdfs_attr';

/** Opaque token charset (plan §14: opaque strings ≤128 chars, validated charset). */
const TOKEN_RE = /^[A-Za-z0-9_.:~-]{1,128}$/;

/**
 * Is this an opaque token we are willing to send? (plan §1: no raw email, ever.)
 *
 * Exported so `core.ts` can hold `data-mdfs-experiment` to the same rule the
 * attribution params are held to — it is publisher-authored markup, and #167
 * found it was the one passthrough with no charset check on either side. The
 * charset has no `@`, so an address cannot pass.
 *
 * This is advice, not enforcement: `/v1/decision` refuses email-shaped
 * passthrough values itself (`_drop_email_shaped`), because a guarantee that
 * lives only in the client we happen to ship is not a guarantee. Keeping it here
 * too is what gives the publisher's developer immediate feedback — the value
 * never leaves the browser, and `log()` shows the payload actually sent.
 */
export function isOpaqueToken(value: string): boolean {
  return TOKEN_RE.test(value);
}

/** Storage can throw (Safari private mode, blocked third-party storage, quota). */
function store(which: 'localStorage' | 'sessionStorage'): Storage | null {
  try {
    const s = window[which];
    // Touch it: in blocked contexts the getter succeeds but access throws.
    s.getItem(SID_KEY);
    return s;
  } catch {
    return null;
  }
}

function read(s: Storage | null, k: string): string | null {
  try {
    return s ? s.getItem(k) : null;
  } catch {
    return null;
  }
}

function write(s: Storage | null, k: string, v: string): void {
  try {
    if (s) s.setItem(k, v);
  } catch {
    /* full or blocked storage degrades to per-pageview ids */
  }
}

/**
 * Opaque random id: `crypto.randomUUID()` when available, then 128 random bits
 * as hex, then a time+Math.random string. The format is deliberately not
 * load-bearing — the server treats it as an opaque token.
 */
export function randomId(): string {
  const c: Crypto | undefined = window.crypto;
  try {
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    if (c && typeof c.getRandomValues === 'function') {
      const b = c.getRandomValues(new Uint8Array(16));
      let out = '';
      for (let i = 0; i < 16; i++) out += (b[i] + 0x100).toString(16).slice(1);
      return out;
    }
  } catch {
    /* fall through to the non-crypto path */
  }
  // Last resort: still opaque, still no PII, just weaker entropy.
  return Date.now().toString(16) + '-' + Math.random().toString(16).slice(2, 12);
}

/** Mint once, then reuse across pageviews and tabs (localStorage). */
export function sessionId(): string {
  const ls = store('localStorage');
  const existing = read(ls, SID_KEY);
  if (existing && TOKEN_RE.test(existing)) return existing;
  const id = randomId();
  write(ls, SID_KEY, id);
  return id;
}

/**
 * Capture the configured inbound params from the URL and persist them in
 * sessionStorage so they survive on-site navigation. A value present in the
 * current URL always wins over a stored one (fresh click beats stale session).
 *
 * Both maps are prototype-free (#109). This is the *request* boundary, and it
 * had the same hole #48/#75 closed on the response: `kept` was a `{}` literal
 * read by key, so a host page's `Object.prototype.campaign` passed both the
 * `typeof` and the charset check and was captured exactly as if it had arrived
 * in the URL — reaching `decisions.campaign_token`, the campaign stats rows,
 * the signed click token and finally the advertiser's own reporting. And the
 * *returned* map is walked with `for...in` by the caller, so an inherited key
 * of any name — `site` and `test` included — reached the payload.
 *
 * The guard is the same structural one, not a second mechanism: the stored
 * value is copied through `ownProperties` rather than adopted (sessionStorage
 * is host-page-writable too, and a `"__proto__"` key in it lands as an ordinary
 * own property on a null-prototype object), and `out` is created with no
 * prototype so nothing downstream can read or enumerate one.
 */
export function attribution(names: string[], search: string): Record<string, string> {
  const ss = store('sessionStorage');
  let kept: Record<string, string> = Object.create(null);
  const raw = read(ss, ATTR_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        kept = ownProperties(parsed as Record<string, string>);
      }
    } catch {
      /* corrupt stored JSON leaves `kept` empty */
    }
  }

  const q = new URLSearchParams(search);
  let dirty = false;
  const out: Record<string, string> = Object.create(null);
  for (let i = 0; i < names.length; i++) {
    const name = names[i];
    const fresh = q.get(name);
    if (fresh && TOKEN_RE.test(fresh)) {
      if (kept[name] !== fresh) dirty = true;
      out[name] = fresh;
    } else if (typeof kept[name] === 'string' && TOKEN_RE.test(kept[name])) {
      out[name] = kept[name];
    }
  }
  if (dirty) write(ss, ATTR_KEY, JSON.stringify(out));
  return out;
}
